import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '../../utils/useFrameP1'; // P1: igual ao do r3f; com ?dbg=1 mede o tempo dos handlers
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { appProfiler } from '../../utils/appProfiler';

interface BoxedTrackerProps {
  position: [number, number, number];
  rotation?: [number, number, number];
  anchorX?: "left" | "right" | "center";
  anchorY?: "top" | "bottom" | "middle";
  w: number;
  h: number;
  fontSize: number;
}

const BoxedTracker = React.forwardRef(({ position, rotation = [0,0,0], anchorX = 'center', anchorY = 'middle', w, h, fontSize }: BoxedTrackerProps, ref: any) => {
  const groupRef = useRef<any>(null);
  const textRef = useRef<any>(null);
  const bgRef = useRef<any>(null);
  const currentTextRef = useRef<string>("");
  const currentColorRef = useRef<string>("");

  const paddingX = 8;
  const paddingY = 2;

  React.useImperativeHandle(ref, () => ({
    get position() { return groupRef.current?.position; },
    set text(t: string) { 
      if (textRef.current && currentTextRef.current !== t) {
        currentTextRef.current = t;
        textRef.current.text = t;
      }
    },
    set width(newW: number) {
      if (bgRef.current) {
        bgRef.current.scale.x = newW / w;
        let newPx = 0;
        if (anchorX === 'left') newPx = newW / 2 - paddingX;
        if (anchorX === 'right') newPx = -newW / 2 + paddingX;
        bgRef.current.position.x = newPx;
      }
    },
    set color(c: string) { 
       if (bgRef.current && currentColorRef.current !== c) {
        currentColorRef.current = c;
        bgRef.current.material.color.set(c); 
       }
    }
  }));

  let bgPx = 0, bgPy = 0;
  if (anchorX === 'left') bgPx = w / 2 - paddingX;
  if (anchorX === 'right') bgPx = -w / 2 + paddingX;
  if (anchorY === 'top') bgPy = -h / 2 + paddingY;
  if (anchorY === 'bottom') bgPy = h / 2 - paddingY;

  return (
    <group ref={groupRef} position={position} rotation={rotation}>
      <mesh ref={bgRef} position={[bgPx, bgPy, 0.1]} renderOrder={30}>
        <planeGeometry args={[w, h]} />
        <meshBasicMaterial toneMapped={true} color="#ffffff" depthTest={true} depthWrite={true} polygonOffset={true} polygonOffsetFactor={-10} polygonOffsetUnits={-10} transparent={false} opacity={1.0} />
      </mesh>
      <Text
        ref={textRef}
        fontSize={fontSize}
        color="#000000"
        anchorX={anchorX}
        anchorY={anchorY}
        position={[0, 0, 0.35]}
        renderOrder={31}
        depthOffset={-12}
      >
        {""}
      </Text>
    </group>
  );
});

interface TrackersProps {
  data: any[];
  getY: (p: number) => number;
  getTimeX: (t: number) => number;
  getForceZ: (m: number) => number;
  wallZ: number;
  frontZ: number;
  floorY: number;
  axisX: number;
  sideX: number;
  colorUp?: string;
  colorDown?: string;
  bottomOfGrid: number;
  backChartWidth?: number;
  startX?: number;
  finalLivePos?: [number, number, number];
  backClippingPlanes?: THREE.Plane[];
  floorClippingPlanes?: THREE.Plane[];
  sideClippingPlanes?: THREE.Plane[];
}

const formatPriceAbbrev = (val: number) => {
  const abs = Math.abs(val);
  if (abs >= 1e6) {
    return `${(val / 1e6).toFixed(1)}M`;
  }
  if (abs >= 1e3) {
    return `${(val / 1e3).toFixed(1)}K`;
  }
  return val.toFixed(2);
};

const formatTimeHHMM = (ts: number) => {
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

export const CurrentValueTrackers: React.FC<TrackersProps> = ({
  data, getY, getTimeX, getForceZ, wallZ, frontZ, floorY, axisX, sideX, colorUp = '#00ffa3', colorDown = '#ff3d67', bottomOfGrid, backChartWidth, startX, finalLivePos,
  backClippingPlanes, floorClippingPlanes, sideClippingPlanes
}) => {
  const priceRef1 = useRef<any>(null); // Back wall price
  const priceRef2 = useRef<any>(null); // Side wall price
  const timeRef1 = useRef<any>(null); // Back wall time
  const timeRef2 = useRef<any>(null); // Floor time
  const forceRef1 = useRef<any>(null); // Side wall force
  const forceRef2 = useRef<any>(null); // Floor force

  // High-precision GPU shader materials for crisp, anti-aliased Dashed Connector Lines
  const unitPlaneGeom = useMemo(() => new THREE.PlaneGeometry(1, 1), []);

  const horizShaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(colorUp) },
        uOpacity: { value: 0.8 },
        uDashSize: { value: 6.0 },
        uGapSize: { value: 4.0 },
      },
      vertexShader: `
        #include <clipping_planes_pars_vertex>
        varying vec3 vWorldPos;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          vec4 mvPosition = viewMatrix * wp;
          #include <clipping_planes_vertex>
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        #include <clipping_planes_pars_fragment>
        uniform vec3 uColor;
        uniform float uOpacity;
        uniform float uDashSize;
        uniform float uGapSize;
        varying vec3 vWorldPos;

        void main() {
          #include <clipping_planes_fragment>
          float total = uDashSize + uGapSize;
          float d = mod(vWorldPos.x, total);
          if (d > uDashSize) discard;
          gl_FragColor = vec4(uColor, uOpacity);
        }
      `,
      clipping: true,
      clippingPlanes: (backClippingPlanes && backClippingPlanes.length > 0) ? backClippingPlanes : null,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      side: THREE.DoubleSide,
    });
  }, []);

  const vertShaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(colorUp) },
        uOpacity: { value: 0.8 },
        uDashSize: { value: 6.0 },
        uGapSize: { value: 4.0 },
      },
      vertexShader: `
        #include <clipping_planes_pars_vertex>
        varying vec3 vWorldPos;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          vec4 mvPosition = viewMatrix * wp;
          #include <clipping_planes_vertex>
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        #include <clipping_planes_pars_fragment>
        uniform vec3 uColor;
        uniform float uOpacity;
        uniform float uDashSize;
        uniform float uGapSize;
        varying vec3 vWorldPos;

        void main() {
          #include <clipping_planes_fragment>
          float total = uDashSize + uGapSize;
          float d = mod(vWorldPos.y, total);
          if (d > uDashSize) discard;
          gl_FragColor = vec4(uColor, uOpacity);
        }
      `,
      clipping: true,
      clippingPlanes: (backClippingPlanes && backClippingPlanes.length > 0) ? backClippingPlanes : null,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      side: THREE.DoubleSide,
    });
  }, []);

  const zShaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(colorUp) },
        uOpacity: { value: 0.8 },
        uDashSize: { value: 6.0 },
        uGapSize: { value: 4.0 },
      },
      vertexShader: `
        #include <clipping_planes_pars_vertex>
        varying vec3 vWorldPos;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          vec4 mvPosition = viewMatrix * wp;
          #include <clipping_planes_vertex>
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        #include <clipping_planes_pars_fragment>
        uniform vec3 uColor;
        uniform float uOpacity;
        uniform float uDashSize;
        uniform float uGapSize;
        varying vec3 vWorldPos;
        void main() {
          #include <clipping_planes_fragment>
          float total = uDashSize + uGapSize;
          float d = mod(vWorldPos.z, total);
          if (d > uDashSize) discard;
          gl_FragColor = vec4(uColor, uOpacity);
        }
      `,
      clipping: true,
      clippingPlanes: (floorClippingPlanes && floorClippingPlanes.length > 0) ? floorClippingPlanes : null,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      side: THREE.DoubleSide,
    });
  }, []);


  const sideHorizShaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(colorUp) },
        uOpacity: { value: 0.8 },
        uDashSize: { value: 6.0 },
        uGapSize: { value: 4.0 },
      },
      vertexShader: `
        #include <clipping_planes_pars_vertex>
        varying vec3 vWorldPos;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          vec4 mvPosition = viewMatrix * wp;
          #include <clipping_planes_vertex>
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        #include <clipping_planes_pars_fragment>
        uniform vec3 uColor;
        uniform float uOpacity;
        uniform float uDashSize;
        uniform float uGapSize;
        varying vec3 vWorldPos;
        void main() {
          #include <clipping_planes_fragment>
          float total = uDashSize + uGapSize;
          float d = mod(vWorldPos.z, total);
          if (d > uDashSize) discard;
          gl_FragColor = vec4(uColor, uOpacity);
        }
      `,
      clipping: true,
      clippingPlanes: (sideClippingPlanes && sideClippingPlanes.length > 0) ? sideClippingPlanes : null,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      side: THREE.DoubleSide,
    });
  }, []);

  const sideVertShaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(colorUp) },
        uOpacity: { value: 0.8 },
        uDashSize: { value: 6.0 },
        uGapSize: { value: 4.0 },
      },
      vertexShader: `
        #include <clipping_planes_pars_vertex>
        varying vec3 vWorldPos;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          vec4 mvPosition = viewMatrix * wp;
          #include <clipping_planes_vertex>
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        #include <clipping_planes_pars_fragment>
        uniform vec3 uColor;
        uniform float uOpacity;
        uniform float uDashSize;
        uniform float uGapSize;
        varying vec3 vWorldPos;
        void main() {
          #include <clipping_planes_fragment>
          float total = uDashSize + uGapSize;
          float d = mod(vWorldPos.y, total);
          if (d > uDashSize) discard;
          gl_FragColor = vec4(uColor, uOpacity);
        }
      `,
      clipping: true,
      clippingPlanes: (sideClippingPlanes && sideClippingPlanes.length > 0) ? sideClippingPlanes : null,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      side: THREE.DoubleSide,
    });
  }, []);

  const floorHorizShaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(colorUp) },
        uOpacity: { value: 0.8 },
        uDashSize: { value: 6.0 },
        uGapSize: { value: 4.0 },
      },
      vertexShader: `
        #include <clipping_planes_pars_vertex>
        varying vec3 vWorldPos;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          vec4 mvPosition = viewMatrix * wp;
          #include <clipping_planes_vertex>
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        #include <clipping_planes_pars_fragment>
        uniform vec3 uColor;
        uniform float uOpacity;
        uniform float uDashSize;
        uniform float uGapSize;
        varying vec3 vWorldPos;
        void main() {
          #include <clipping_planes_fragment>
          float total = uDashSize + uGapSize;
          float d = mod(vWorldPos.x, total);
          if (d > uDashSize) discard;
          gl_FragColor = vec4(uColor, uOpacity);
        }
      `,
      clipping: true,
      clippingPlanes: (floorClippingPlanes && floorClippingPlanes.length > 0) ? floorClippingPlanes : null,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      side: THREE.DoubleSide,
    });
  }, []);

  useEffect(() => {
    const backPlanes = (backClippingPlanes && backClippingPlanes.length > 0) ? backClippingPlanes : null;
    horizShaderMaterial.clippingPlanes = backPlanes;
    vertShaderMaterial.clippingPlanes = backPlanes;
    horizShaderMaterial.needsUpdate = true;
    vertShaderMaterial.needsUpdate = true;

    const floorPlanes = (floorClippingPlanes && floorClippingPlanes.length > 0) ? floorClippingPlanes : null;
    floorHorizShaderMaterial.clippingPlanes = floorPlanes;
    zShaderMaterial.clippingPlanes = floorPlanes;
    floorHorizShaderMaterial.needsUpdate = true;
    zShaderMaterial.needsUpdate = true;

    const sidePlanes = (sideClippingPlanes && sideClippingPlanes.length > 0) ? sideClippingPlanes : null;
    sideHorizShaderMaterial.clippingPlanes = sidePlanes;
    sideVertShaderMaterial.clippingPlanes = sidePlanes;
    sideHorizShaderMaterial.needsUpdate = true;
    sideVertShaderMaterial.needsUpdate = true;
  }, [
    backClippingPlanes, floorClippingPlanes, sideClippingPlanes,
    horizShaderMaterial, vertShaderMaterial, floorHorizShaderMaterial, zShaderMaterial, sideHorizShaderMaterial, sideVertShaderMaterial
  ]);

  const meshHRef = useRef<THREE.Mesh>(null);
  const meshVRef = useRef<THREE.Mesh>(null);
  const meshFloorHRef = useRef<THREE.Mesh>(null);
  const meshFloorVRef = useRef<THREE.Mesh>(null);
  const meshSideHRef = useRef<THREE.Mesh>(null);
  const meshSideVRef = useRef<THREE.Mesh>(null);

  const camTargetRef = useRef(new THREE.Vector3());

  useEffect(() => {
    return () => {
      unitPlaneGeom.dispose();
      horizShaderMaterial.dispose();
      vertShaderMaterial.dispose();
      zShaderMaterial.dispose();
      floorHorizShaderMaterial.dispose();
      sideHorizShaderMaterial.dispose();
      sideVertShaderMaterial.dispose();
    };
  }, [unitPlaneGeom, horizShaderMaterial, vertShaderMaterial, zShaderMaterial, floorHorizShaderMaterial, sideHorizShaderMaterial, sideVertShaderMaterial]);
  
  useFrame(({ camera }) => {
    appProfiler.time('useFrame:CurrentValueTrackers', () => {
    if (!data || data.length === 0) return;
    const last = data[data.length - 1];
    const prev = data.length > 1 ? data[data.length - 2] : last;
    const isUp = last.close >= prev.close;
    const priceColor = isUp ? colorUp : colorDown;
    const forceColor = (last.momentum || 0) >= 0 ? colorUp : colorDown;
    const timeColorCandles = priceColor;
    const timeColorFloor = forceColor;
    
    const y = getY(last.close);
    const floorX = getTimeX(last.timestamp);
    const effBackWidth = backChartWidth ?? (axisX - (startX ?? 0));
    const effStartX = startX ?? (axisX - effBackWidth);
    const backCandleX = effStartX + (last.time !== undefined ? last.time : 1) * effBackWidth;
    
    const forceWorldZ = getForceZ(last.momentum || 0);
    
    // Wave precise endpoints (matches exact tip of the spline)
    const [liveX, liveY, liveZ] = finalLivePos || [floorX, y, forceWorldZ];
    
    const mom = last.momentum || 0;
    let forceStr;
    if (Math.abs(mom) >= 1000) {
      forceStr = (mom / 1000).toFixed(1) + 'K';
    } else {
      forceStr = mom.toFixed(1);
    }
    
    // 1. Back Wall live price label (inner face, price only, formatted with K)
    if (priceRef1.current) {
      priceRef1.current.position.y = y;
      const t = formatPriceAbbrev(last.close);
      if (priceRef1.current.text !== t) priceRef1.current.text = t;
      if (priceRef1.current.color !== priceColor) priceRef1.current.color = priceColor;
    }

    // 2. Side Wall live price label
    if (priceRef2.current) {
      priceRef2.current.position.y = y;
      const t = formatPriceAbbrev(last.close);
      if (priceRef2.current.text !== t) priceRef2.current.text = t;
      if (priceRef2.current.color !== priceColor) priceRef2.current.color = priceColor;
    }

    // 3. Back Wall live time label (inner face, HH:MM format)
    if (timeRef1.current) {
      timeRef1.current.position.x = backCandleX;
      const t = formatTimeHHMM(last.timestamp);
      if (timeRef1.current.text !== t) timeRef1.current.text = t;
      if (timeRef1.current.color !== timeColorCandles) timeRef1.current.color = timeColorCandles;
    }

    // 4. Floor live time label
    if (timeRef2.current) {
      timeRef2.current.position.x = floorX;
      const t = formatTimeHHMM(last.timestamp);
      if (timeRef2.current.text !== t) timeRef2.current.text = t;
      if (timeRef2.current.color !== timeColorFloor) timeRef2.current.color = timeColorFloor;
    }

    // 5. Force labels
    if (forceRef1.current) {
      forceRef1.current.position.z = forceWorldZ; 
      if (forceRef1.current.text !== forceStr) forceRef1.current.text = forceStr;
      if (forceRef1.current.color !== forceColor) forceRef1.current.color = forceColor;
    }

    if (forceRef2.current) {
      forceRef2.current.position.z = forceWorldZ;
      if (forceRef2.current.text !== forceStr) forceRef2.current.text = forceStr;
      if (forceRef2.current.color !== forceColor) forceRef2.current.color = forceColor;
    }

    // 6. Update Back Wall horizontal and vertical dashed connector lines
    const targetPriceX = axisX - 10;
    const targetTimeY = bottomOfGrid + 38;
    const lineZ = wallZ + 1.2;

    // Calculate screen-constant thickness and dash sizing based on camera distance (exact pixel-to-world ratio)
    camTargetRef.current.set(backCandleX, y, wallZ);
    const camDist = camera.position.distanceTo(camTargetRef.current);
    const persCam = camera as THREE.PerspectiveCamera;
    const fovRad = ((persCam.fov || 50) * Math.PI) / 180;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
    const pxUnit = (2 * Math.tan(fovRad / 2) * camDist) / Math.max(screenHeight, 300);

    // Screen-space constants: 1.3px line thickness, 5.5px dash length, 4.0px gap length
    const adaptiveThickness = Math.max(0.03, 1.3 * pxUnit);
    const adaptiveDash = Math.max(0.1, 5.5 * pxUnit);
    const adaptiveGap = Math.max(0.08, 4.0 * pxUnit);

    horizShaderMaterial.uniforms.uOpacity.value = 0.8;
    horizShaderMaterial.uniforms.uDashSize.value = adaptiveDash;
    horizShaderMaterial.uniforms.uGapSize.value = adaptiveGap;
    vertShaderMaterial.uniforms.uOpacity.value = 0.8;
    vertShaderMaterial.uniforms.uDashSize.value = adaptiveDash;
    vertShaderMaterial.uniforms.uGapSize.value = adaptiveGap;

    // Horizontal Dashed Ribbon (from backCandleX to targetPriceX at height y)
    if (meshHRef.current) {
      const minX = Math.min(backCandleX, targetPriceX);
      const maxX = Math.max(backCandleX, targetPriceX);
      const len = Math.max(0.1, maxX - minX);
      const centerX = (minX + maxX) / 2;
      meshHRef.current.position.set(centerX, y, lineZ);
      meshHRef.current.scale.set(len, adaptiveThickness, 1);
      horizShaderMaterial.uniforms.uColor.value.set(priceColor);
    }

    // Vertical Dashed Ribbon (from backCandleX from targetTimeY up to y)
    if (meshVRef.current) {
      const minY = Math.min(y, targetTimeY);
      const maxY = Math.max(y, targetTimeY);
      const len = Math.max(0.1, maxY - minY);
      const centerX = (minY + maxY) / 2;
      meshVRef.current.position.set(backCandleX, centerX, lineZ);
      meshVRef.current.scale.set(adaptiveThickness, len, 1);
      vertShaderMaterial.uniforms.uColor.value.set(priceColor);
    }

    // Floor Dash Settings
    floorHorizShaderMaterial.uniforms.uOpacity.value = 0.8;
    floorHorizShaderMaterial.uniforms.uDashSize.value = adaptiveDash;
    floorHorizShaderMaterial.uniforms.uGapSize.value = adaptiveGap;
    
    zShaderMaterial.uniforms.uOpacity.value = 0.8;
    zShaderMaterial.uniforms.uDashSize.value = adaptiveDash;
    zShaderMaterial.uniforms.uGapSize.value = adaptiveGap;

    // Floor Horizontal Dashed Ribbon (from liveX to axisX - 15 at liveZ)
    if (meshFloorHRef.current) {
      const targetForceX = axisX - 15;
      const minX = Math.min(liveX, targetForceX);
      const maxX = Math.max(liveX, targetForceX);
      const len = Math.max(0.1, maxX - minX);
      const centerX = (minX + maxX) / 2;
      meshFloorHRef.current.position.set(centerX, floorY, liveZ);
      meshFloorHRef.current.scale.set(len, adaptiveThickness, 1);
      // To orient horizontally on the floor, we rotate around X so plane lies flat
      meshFloorHRef.current.rotation.set(-Math.PI / 2, 0, 0);
      floorHorizShaderMaterial.uniforms.uColor.value.set(forceColor);
    }

    // Floor Vertical Dashed Ribbon (from liveZ to frontZ - 15 at liveX)
    if (meshFloorVRef.current) {
      const targetTimeZ = frontZ - 15;
      const minZ = Math.min(liveZ, targetTimeZ);
      const maxZ = Math.max(liveZ, targetTimeZ);
      const len = Math.max(0.1, maxZ - minZ);
      const centerZ = (minZ + maxZ) / 2;
      meshFloorVRef.current.position.set(liveX, floorY, centerZ);
      meshFloorVRef.current.scale.set(adaptiveThickness, len, 1);
      meshFloorVRef.current.rotation.set(-Math.PI / 2, 0, 0);
      zShaderMaterial.uniforms.uColor.value.set(forceColor);
    }

    // Side Wall Dash Settings
    sideHorizShaderMaterial.uniforms.uOpacity.value = 0.8;
    sideHorizShaderMaterial.uniforms.uDashSize.value = adaptiveDash;
    sideHorizShaderMaterial.uniforms.uGapSize.value = adaptiveGap;
    
    sideVertShaderMaterial.uniforms.uOpacity.value = 0.8;
    sideVertShaderMaterial.uniforms.uDashSize.value = adaptiveDash;
    sideVertShaderMaterial.uniforms.uGapSize.value = adaptiveGap;

    // Side Wall Horizontal Dashed Ribbon (from liveZ to -frontZ + 15 at height liveY)
    if (meshSideHRef.current) {
      const targetSidePriceZ = -frontZ + 15;
      const minZ = Math.min(liveZ, targetSidePriceZ);
      const maxZ = Math.max(liveZ, targetSidePriceZ);
      const len = Math.max(0.1, maxZ - minZ);
      const centerZ = (minZ + maxZ) / 2;
      meshSideHRef.current.position.set(sideX + 0.5, liveY, centerZ);
      meshSideHRef.current.scale.set(len, adaptiveThickness, 1);
      meshSideHRef.current.rotation.set(0, Math.PI / 2, 0);
      sideHorizShaderMaterial.uniforms.uColor.value.set(priceColor);
    }

    // Side Wall Vertical Dashed Ribbon (from targetTimeY up to liveY at liveZ)
    if (meshSideVRef.current) {
      const targetSideForceY = bottomOfGrid + 15;
      const minY = Math.min(liveY, targetSideForceY);
      const maxY = Math.max(liveY, targetSideForceY);
      const len = Math.max(0.1, maxY - minY);
      const centerY = (minY + maxY) / 2;
      meshSideVRef.current.position.set(sideX + 0.5, centerY, liveZ);
      meshSideVRef.current.scale.set(adaptiveThickness, len, 1);
      meshSideVRef.current.rotation.set(0, Math.PI / 2, 0);
      sideVertShaderMaterial.uniforms.uColor.value.set(forceColor);
    }
    });
  });

  return (
    <group>
      {/* Back Wall Dashed Connectors (GPU anti-aliased ribbons with depth testing) */}
      <mesh ref={meshHRef} geometry={unitPlaneGeom} material={horizShaderMaterial} />
      <mesh ref={meshVRef} geometry={unitPlaneGeom} material={vertShaderMaterial} />
      
      {/* Floor Dashed Connectors */}
      <mesh ref={meshFloorHRef} geometry={unitPlaneGeom} material={floorHorizShaderMaterial} />
      <mesh ref={meshFloorVRef} geometry={unitPlaneGeom} material={zShaderMaterial} />

      {/* Side Wall Dashed Connectors */}
      <mesh ref={meshSideHRef} geometry={unitPlaneGeom} material={sideHorizShaderMaterial} />
      <mesh ref={meshSideVRef} geometry={unitPlaneGeom} material={sideVertShaderMaterial} />

      {/* Back Wall Price (Inside face) */}
      <BoxedTracker ref={priceRef1} position={[axisX - 15, 0, wallZ + 1.0]} anchorX="right" anchorY="middle" w={64} h={28} fontSize={20} />
      
      {/* Side Wall Price (Moved to back/right side) */}
      <BoxedTracker ref={priceRef2} position={[sideX + 1.0, 0, -frontZ + 15]} rotation={[0, Math.PI / 2, 0]} anchorX="right" anchorY="middle" w={64} h={28} fontSize={20} />
      
      {/* Back Wall Time (Inside face) */}
      <BoxedTracker ref={timeRef1} position={[0, bottomOfGrid + 15, wallZ + 1.0]} anchorX="center" anchorY="bottom" w={64} h={28} fontSize={20} />
      
      {/* Floor Time */}
      <BoxedTracker ref={timeRef2} position={[0, floorY + 1.0, frontZ - 15]} rotation={[-Math.PI / 2, 0, 0]} anchorX="center" anchorY="bottom" w={64} h={28} fontSize={20} />
      
      {/* Side Wall Force */}
      <BoxedTracker ref={forceRef1} position={[sideX + 1.0, bottomOfGrid + 15, 0]} rotation={[0, Math.PI / 2, 0]} anchorX="center" anchorY="bottom" w={96} h={28} fontSize={18} />
      
      {/* Floor Force */}
      <BoxedTracker ref={forceRef2} position={[axisX - 15, floorY + 1.0, 0]} rotation={[-Math.PI / 2, 0, 0]} anchorX="right" anchorY="middle" w={64} h={28} fontSize={20} />
    </group>
  );
};
